import { haveConflictingFormatIdentity, isRepeatableProductFormat } from '../../utils/varietyFormatIdentity';

const toTrimmedString = (value) => String(value ?? '').trim();

const parseOptionalNumber = (value) => {
  const normalized = toTrimmedString(value);
  if (!normalized) return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const isPositiveNumber = (value) => {
  const parsed = parseOptionalNumber(value);
  return parsed !== null && parsed > 0;
};

export const buildInitialProductDraft = () => ({
  _draftKey: 'product',
  productName: '',
  categoryId: '',
  subcategoryId: '',
  barcodeScope: 'global',
  useDefaultTaxes: true,
  taxIds: [],
  imageFile: null,
});

export const buildVarietyDraft = (draftKey, { isResaleOnly = false } = {}) => ({
  _draftKey: String(draftKey),
  varietyName: '',
  description: '',
  inventoryBaseUnit: isResaleOnly ? 'unidad' : 'kg',
  imageFile: null,
  formats: [],
});

export const buildFormatDraft = (draftKey, productFormatCode = 'detalle') => ({
  _draftKey: String(draftKey),
  productFormatCode,
  metricUnit: '',
  description: '',
  barcode: '',
  barcodeIsSuggested: false,
  unitWeightKg: '',
  baseQuantityPerUnit: '',
  useScaleWeight: false,
  imageFile: null,
});

export const resolveSelectedCategory = (productDraft, categories = []) => (
  categories.find((category) => String(category?.id) === String(productDraft?.categoryId)) || null
);

export const validateProductDraft = (productDraft, categories = []) => {
  const errors = {};
  const selectedCategory = resolveSelectedCategory(productDraft, categories);
  if (!toTrimmedString(productDraft?.productName)) {
    errors.productName = 'El nombre del producto es obligatorio.';
  }
  if (!selectedCategory) {
    errors.categoryId = 'Selecciona una categoría.';
  }
  if (!['global', 'per_variety'].includes(productDraft?.barcodeScope)) {
    errors.barcodeScope = 'Selecciona un alcance de código de barras válido.';
  }
  if (productDraft?.subCategoryId || productDraft?.subcategoryId) {
    const subcategoryId = productDraft?.subCategoryId || productDraft?.subcategoryId;
    const belongsToCategory = (selectedCategory?.CategorySubcategories || []).some(
      (subcategory) => String(subcategory?.id) === String(subcategoryId)
    );
    if (!belongsToCategory) errors.subcategoryId = 'La subcategoría no pertenece a la categoría.';
  }
  return errors;
};

export const validateVarietyDrafts = (varieties = []) => {
  const errors = {};
  if (!varieties.length) {
    errors._form = 'Agrega al menos una variedad.';
    return errors;
  }
  const seenNames = new Set();
  varieties.forEach((variety) => {
    const rowErrors = {};
    const name = toTrimmedString(variety?.varietyName);
    if (!name) rowErrors.varietyName = 'El nombre de la variedad es obligatorio.';
    const normalizedName = name.toLocaleLowerCase('es');
    if (name && seenNames.has(normalizedName)) {
      rowErrors.varietyName = 'Usa un nombre distinto para cada variedad.';
    }
    if (name) seenNames.add(normalizedName);
    if (!toTrimmedString(variety?.inventoryBaseUnit)) {
      rowErrors.inventoryBaseUnit = 'La unidad base es obligatoria.';
    }
    if (Object.keys(rowErrors).length) errors[variety?._draftKey] = rowErrors;
  });
  return errors;
};

export const validateFormatDrafts = ({
  varieties = [],
  formatOptions = [],
} = {}) => {
  const errors = {};
  const allowedFormatCodes = new Set(formatOptions.map((option) => option.value));
  const seenBarcodes = new Set();
  varieties.forEach((variety) => {
    const formats = Array.isArray(variety?.formats) ? variety.formats : [];
    if (!formats.length) {
      if (!variety?.formatsInitialized) errors[variety?._draftKey] = { _form: 'Agrega al menos un formato para esta variedad.' };
      return;
    }
    formats.forEach((format) => {
      const rowErrors = {};
      if (!allowedFormatCodes.has(format?.productFormatCode)) {
        rowErrors.productFormatCode = 'Selecciona un formato activo.';
      } else if (formats.some((entry) => entry !== format && haveConflictingFormatIdentity(entry, format, formatOptions))) {
        rowErrors.productFormatCode = isRepeatableProductFormat(format, formatOptions)
          ? 'Esta variedad ya tiene un pack con la misma unidad y peso.'
          : 'Esta variedad ya tiene ese tipo de formato.';
      }
      const barcode = toTrimmedString(format?.barcode);
      if (barcode && !/^\d{1,7}$/.test(barcode)) {
        rowErrors.barcode = 'Usa hasta 7 dígitos.';
      } else if (barcode) {
        const barcodeKey = String(Number(barcode));
        if (seenBarcodes.has(barcodeKey)) {
          rowErrors.barcode = 'Este código ya se usa en otro formato del borrador.';
        }
        seenBarcodes.add(barcodeKey);
      }
      if (toTrimmedString(format?.unitWeightKg) && !isPositiveNumber(format.unitWeightKg)) {
        rowErrors.unitWeightKg = 'Debe ser un número mayor a 0.';
      }
      if (Object.keys(rowErrors).length) {
        errors[variety?._draftKey] = {
          ...(errors[variety?._draftKey] || {}),
          [format?._draftKey]: rowErrors,
        };
      }
    });
  });
  return errors;
};

const optionalNumericPayloadValue = (value) => (
  toTrimmedString(value) === '' ? null : Number(value)
);

export const buildProductPayload = (productDraft, categories = []) => {
  const category = resolveSelectedCategory(productDraft, categories);
  const subcategory = (category?.CategorySubcategories || []).find(
    (entry) => String(entry?.id) === String(productDraft?.subcategoryId)
  );
  return {
    productName: toTrimmedString(productDraft?.productName),
    categoryId: category?.id,
    subcategoryId: subcategory?.id ?? null,
    barcodeScope: productDraft?.barcodeScope === 'per_variety' ? 'per_variety' : 'global',
    useDefaultTaxes: productDraft?.useDefaultTaxes !== false,
    taxIds: productDraft?.useDefaultTaxes === false ? [...(productDraft?.taxIds || [])] : [],
  };
};

export const buildVarietyPayload = (varietyDraft, productId) => ({
  productId,
  varietyName: toTrimmedString(varietyDraft?.varietyName),
  description: toTrimmedString(varietyDraft?.description) || null,
  inventoryBaseUnit: toTrimmedString(varietyDraft?.inventoryBaseUnit) || 'kg',
});

export const buildFormatPayload = ({ formatDraft, varietyId }) => ({
  varietyId,
  productFormatCode: formatDraft?.productFormatCode,
  metricUnit: toTrimmedString(formatDraft?.metricUnit) || null,
  description: toTrimmedString(formatDraft?.description) || null,
  barcode: toTrimmedString(formatDraft?.barcode) || null,
  barcodeIsSuggested: Boolean(formatDraft?.barcodeIsSuggested),
  useScaleWeight: Boolean(formatDraft?.useScaleWeight),
  unitWeightKg: optionalNumericPayloadValue(formatDraft?.unitWeightKg),
  baseQuantityPerUnit: optionalNumericPayloadValue(formatDraft?.baseQuantityPerUnit),
});

export const hasValidationErrors = (errors) => Boolean(
  errors && Object.keys(errors).length
);
